import React, { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { PaddedContainer, SPACING, Text } from "@disco/disco_core";

import EngageSecondaryTile from "../EngageWidget/EngageSecondaryTile";
import { OFFER_TYPE } from "../../conf";
import useTimer from "../../hooks/useTimer";
import copyClipboard from "../../utils/copyClipboard";

export const FLIP_DURATION = 0.6;

const FLIP_DURATION_IN_MS = FLIP_DURATION * 1000;
export const REDIRECT_DELAY = FLIP_DURATION_IN_MS + 950;

const cardVariants = {
	front: {
		rotateY: 0,
		transition: { duration: FLIP_DURATION, ease: "easeInOut" },
	},
	back: {
		rotateY: 180,
		transition: { duration: FLIP_DURATION, ease: "easeInOut" },
	},
};

const FlippableOfferCard = ({
	product,
	brand,
	onClick: onClickProp,
	setContainerRef,
	trackableId,
}) => {
	const [isFlipped, setIsFlipped] = useState(false);

	const {
		id: brandId,
		incentive_type: incentiveType,
		incentive_heading: incentiveHeading,
		incentive_code: incentiveCode,
	} = brand;

	const hasOffer = !!incentiveType;

	const onClick = () => {
		if (hasOffer) setIsFlipped(true); // Add support for dynamic condition to allow/disable flip
		if (hasOffer && incentiveType === OFFER_TYPE.PROMO_CODE)
			copyClipboard(incentiveCode);
		onClickProp?.(isFlipped);
	};

	const getUnclaimedSubheading = () => {
		const DEFAULT_UNCLAIMED_SUBHEADING = "Explore more on…";

		if (hasOffer) {
			return incentiveHeading || DEFAULT_UNCLAIMED_SUBHEADING;
		}

		return DEFAULT_UNCLAIMED_SUBHEADING;
	};

	const unclaimedSubheading = getUnclaimedSubheading();

	return (
		<motion.div
			data-testid={`essential-flippable-offer-card-${brandId}`}
			data-intersectionuid={trackableId}
			style={{
				perspective: 1000,
				cursor: "pointer",
				width: "100%",
				display: "block",
			}}
			onClick={onClick}
			ref={(ref) => setContainerRef(ref)}
		>
			<motion.div
				style={{
					width: "100%",
					position: "relative",
					transformStyle: "preserve-3d",
				}}
				animate={isFlipped ? "back" : "front"}
				variants={cardVariants}
			>
				{/* Front of card */}
				<motion.div
					style={{
						width: "100%",
						backfaceVisibility: "hidden",
						WebkitBackfaceVisibility: "hidden",
						position: "relative",
						transform: isFlipped
							? "rotateY(180deg)"
							: "rotateY(0deg)",
						opacity: isFlipped ? 0 : 1,
						transition: isFlipped
							? `opacity ${
									FLIP_DURATION / 2
							  }s ease-in, transform ${FLIP_DURATION}s ease-in-out`
							: `opacity ${FLIP_DURATION / 2}s ease-out ${
									FLIP_DURATION / 2
							  }s, transform ${FLIP_DURATION}s ease-in-out`,
					}}
				>
					<EngageSecondaryTile
						product={product}
						brand={brand}
						subheading={unclaimedSubheading}
					/>
				</motion.div>

				{/* Back of card */}
				<motion.div
					style={{
						backfaceVisibility: "hidden",
						WebkitBackfaceVisibility: "hidden",
						overflow: "hidden",
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						width: "100%",
						height: "100%",
						transform: "rotateY(180deg)",
						opacity: isFlipped ? 1 : 0,
						transition: isFlipped
							? `opacity ${FLIP_DURATION / 2}s ease-out ${
									FLIP_DURATION / 2
							  }s, transform ${FLIP_DURATION}s ease-in-out`
							: `opacity ${
									FLIP_DURATION / 2
							  }s ease-in, transform ${FLIP_DURATION}s ease-in-out`,
					}}
				>
					{hasOffer ? (
						<ClaimedOffer
							isActive={isFlipped}
							product={product}
							brand={brand}
						/>
					) : (
						<EngageSecondaryTile
							product={product}
							brand={brand}
							subheading={unclaimedSubheading}
						/>
					)}
				</motion.div>
			</motion.div>
		</motion.div>
	);
};

function ClaimedOffer({ isActive, product, brand }) {
	const {
		incentive_type: incentiveType,
		incentive_heading: incentiveHeading,
		incentive_subheading: incentiveSubheading,
		incentive_code: incentiveCode,
	} = brand;

	const hasPromoCode = incentiveType === OFFER_TYPE.PROMO_CODE;

	const [showCode, setShowCode] = useState(false);

	const handleOnComplete = useCallback(() => setShowCode(true), []);

	useTimer({
		tickSize: 1,
		numTicks: REDIRECT_DELAY / 2,
		onComplete: handleOnComplete,
		playing: isActive,
	});

	return (
		<EngageSecondaryTile
			product={product}
			brand={brand}
			fullGradient={true}
			gradientOpacity={0.9}
		>
			{({ rawBgColorString, isRawBgDark }) => {
				const darkTranslucentColor = "rgba(0, 0, 0, 0.8)";
				const lightTranslucentColor = "rgba(255, 255, 255, 0.8)";

				const complementaryColor = isRawBgDark
					? darkTranslucentColor
					: lightTranslucentColor;

				const contrastColor = isRawBgDark
					? lightTranslucentColor
					: darkTranslucentColor;

				return (
					<PaddedContainer
						style={{
							flexGrow: 1,
							flexDirection: "column",
							justifyContent: "space-between",
						}}
						flexContent
					>
						<PaddedContainer marginTop={SPACING.TINY}>
							<Text size={"body"} thick>
								{incentiveHeading}
							</Text>
							<Text
								size={"label"}
								style={{ fontSize: "12px" }}
								thin
								// style={{
								// 	height: "calc(2 * var(--disco-fh-label))",
								// }}
							>
								{incentiveSubheading}
							</Text>
						</PaddedContainer>
						<Text
							size="micro"
							style={{
								fontSize: "11px",
								fontWeight: 400,
								background: `rgba(${rawBgColorString}, 0.9)`,
								color: contrastColor,
								borderRadius: "var(--disco-curve-sm)",
							}}
							hPadding={SPACING.TINY}
							ellipsis
							center
							vPadding={SPACING.SMALL}
						>
							<b>
								{hasPromoCode
									? showCode
										? incentiveCode
										: "Code copied"
									: "Shop now"}
							</b>
						</Text>
					</PaddedContainer>
				);
			}}
		</EngageSecondaryTile>
	);
}

export default FlippableOfferCard;
